import { useState, useEffect, useRef, useCallback } from 'react';
import { Send, CheckCheck, PenLine, Layers, Tag, CheckSquare, X, Loader2, ListFilter } from 'lucide-react';
import { supabase } from '../services/supabase';
import { logger } from '../services/logger';
import { generateSmartPrompts, detectTags, expandEntry, extractActionItems } from '../services/journal-ai';

interface JournalPrompt {
  id: string;
  text: string;
  category: string;
  source?: string;
}

const MOODS = [
  { id: 'energized', label: 'Energized', color: 'from-amber-400 to-orange-500', bg: 'bg-amber-50', text: 'text-amber-700' },
  { id: 'calm', label: 'Calm', color: 'from-sky-400 to-blue-500', bg: 'bg-sky-50', text: 'text-sky-700' },
  { id: 'focused', label: 'Focused', color: 'from-emerald-400 to-green-500', bg: 'bg-emerald-50', text: 'text-emerald-700' },
  { id: 'anxious', label: 'Anxious', color: 'from-rose-400 to-red-500', bg: 'bg-rose-50', text: 'text-rose-700' },
  { id: 'tired', label: 'Tired', color: 'from-slate-400 to-gray-500', bg: 'bg-slate-50', text: 'text-slate-700' },
  { id: 'grateful', label: 'Grateful', color: 'from-teal-400 to-cyan-500', bg: 'bg-teal-50', text: 'text-teal-700' }
];

export function QuickJournal({ onSave }: { onSave?: () => void }) {
  const [entry, setEntry] = useState('');
  const [selectedMood, setSelectedMood] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [smartPrompts, setSmartPrompts] = useState<JournalPrompt[]>([]);
  const [loadingPrompts, setLoadingPrompts] = useState(true);
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null);
  const [suggestedTags, setSuggestedTags] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [expandQuestion, setExpandQuestion] = useState<string | null>(null);
  const [loadingExpand, setLoadingExpand] = useState(false);
  const [detectedActions, setDetectedActions] = useState<string[]>([]);
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [weeklyValues, setWeeklyValues] = useState<string[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const tagDebounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setMounted(true);
    loadUserAndPrompts();
  }, []);

  async function loadUserAndPrompts() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
        const prompts = await generateSmartPrompts(user.id);
        setSmartPrompts(prompts);

        const { data: profile } = await supabase
          .from('profiles')
          .select('weekly_plan_values, weekly_plan_locked_at')
          .eq('id', user.id)
          .maybeSingle();

        if (profile?.weekly_plan_locked_at && profile.weekly_plan_values) {
          const lockedDate = new Date(profile.weekly_plan_locked_at);
          const weekStart = new Date();
          weekStart.setDate(weekStart.getDate() - weekStart.getDay());

          if (lockedDate >= weekStart) {
            setWeeklyValues(profile.weekly_plan_values.slice(0, 3));
          }
        }
      }
    } catch (err) {
      logger.error('Error loading prompts:', err);
    } finally {
      setLoadingPrompts(false);
    }
  }

  const handleEntryChange = useCallback((value: string) => {
    setEntry(value);

    if (tagDebounceRef.current) {
      clearTimeout(tagDebounceRef.current);
    }

    tagDebounceRef.current = setTimeout(() => {
      if (value.length > 15) {
        const detected = detectTags(value);
        const newSuggestions = detected.filter(tag => !selectedTags.includes(tag));
        setSuggestedTags(newSuggestions);
        setShowTagSuggestions(newSuggestions.length > 0);

        const actions = extractActionItems(value);
        setDetectedActions(actions);
      } else {
        setSuggestedTags([]);
        setShowTagSuggestions(false);
        setDetectedActions([]);
      }
    }, 500);
  }, [selectedTags]);

  const handlePromptClick = (prompt: JournalPrompt) => {
    setSelectedPrompt(prompt.text);
    setEntry('');
    textareaRef.current?.focus();
  };

  const handleExpandClick = async () => {
    if (!entry.trim() || !userId || loadingExpand) return;

    setLoadingExpand(true);
    try {
      const question = await expandEntry(entry.trim(), userId);
      setExpandQuestion(question);
    } catch (err) {
      logger.error('Error expanding entry:', err);
    } finally {
      setLoadingExpand(false);
    }
  };

  const handleTagClick = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
      setSuggestedTags(suggestedTags.filter(t => t !== tag));
    }
  };

  const handleActionToggle = (action: string) => {
    if (selectedActions.includes(action)) {
      setSelectedActions(selectedActions.filter(a => a !== action));
    } else {
      setSelectedActions([...selectedActions, action]);
    }
  };

  const minChars = 20;
  const hasMinChars = entry.trim().length >= minChars;
  const canSave = entry.trim().length > 0;

  const handleSave = async () => {
    if (!entry.trim()) return;

    try {
      setSaving(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const allTags = [...new Set([...selectedTags, ...(selectedMood ? [selectedMood] : [])])];

      const { data: journalEntry, error } = await supabase
        .from('journal_entries')
        .insert({
          user_id: user.id,
          date: new Date().toISOString().split('T')[0],
          entry_type: 'quick',
          quick_entry: entry.trim(),
          tags: allTags,
          mood: selectedMood || 'neutral',
          is_ai_analyzed: false,
          prompt_source: selectedPrompt || null
        })
        .select()
        .single();

      if (error) throw error;

      if (selectedActions.length > 0 && journalEntry) {
        const actionInserts = selectedActions.map(action => ({
          user_id: user.id,
          action_text: action,
          source: 'journal',
          source_id: journalEntry.id,
          committed_date: new Date().toISOString(),
          status: 'pending'
        }));

        await supabase
          .from('coaching_action_items')
          .insert(actionInserts);
      }

      setSaved(true);
      setEntry('');
      setSelectedMood(null);
      setSelectedTags([]);
      setSelectedPrompt(null);
      setExpandQuestion(null);
      setDetectedActions([]);
      setSelectedActions([]);

      if (onSave) onSave();

      setTimeout(() => {
        setSaved(false);
        loadUserAndPrompts();
      }, 3000);
    } catch (err) {
      logger.error('Error saving quick journal entry:', err);
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-50 via-orange-50 to-amber-50 p-8 border border-amber-200/50 transition-all duration-500 ${mounted ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
        <div className="absolute top-0 right-0 w-32 h-32 bg-amber-200/30 rounded-full -mr-16 -mt-16 blur-2xl" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-orange-200/20 rounded-full -ml-12 -mb-12 blur-xl" />

        <div className="relative text-center space-y-4">
          <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-amber-200">
            <CheckCheck className="h-8 w-8 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-1">Captured</h3>
            <p className="text-gray-600">
              {selectedActions.length > 0
                ? `Your thoughts are saved with ${selectedActions.length} action item${selectedActions.length > 1 ? 's' : ''}.`
                : 'Your thoughts are saved. Keep going.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const selectedMoodData = selectedMood ? MOODS.find(m => m.id === selectedMood) : null;
  const showSmartPrompts = !entry && !selectedPrompt && smartPrompts.length > 0;

  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-gray-200/80 overflow-hidden transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 bg-gradient-to-br from-gray-100 to-gray-50 rounded-xl flex items-center justify-center">
              <PenLine className="h-5 w-5 text-gray-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-gray-900">Quick Journal</h3>
                {weeklyValues.length > 0 && (
                  <div className="flex items-center gap-1">
                    {weeklyValues.map((value) => (
                      <span key={value} className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded border border-amber-200/50">
                        {value}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-500">
                {weeklyValues.length > 0 ? 'How are you living these values today?' : 'Capture your thoughts'}
              </p>
            </div>
          </div>
          {entry.length > 30 && !loadingExpand && !expandQuestion && (
            <button
              onClick={handleExpandClick}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors"
            >
              <Layers className="h-4 w-4" />
              <span>Go deeper</span>
            </button>
          )}
          {loadingExpand && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Thinking...</span>
            </div>
          )}
        </div>

        {showSmartPrompts && (
          <div className="mb-4">
            <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
              <ListFilter className="h-3 w-3" />
              Tap to start
            </p>
            <div className="flex flex-wrap gap-2">
              {loadingPrompts ? (
                <div className="text-sm text-gray-400">Loading prompts...</div>
              ) : (
                smartPrompts.map((prompt) => (
                  <button
                    key={prompt.id}
                    onClick={() => handlePromptClick(prompt)}
                    className="px-3 py-2 text-sm text-left bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 hover:border-gray-300 transition-all text-gray-700 hover:text-gray-900"
                  >
                    {prompt.text}
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        <div className="flex gap-2 mb-4 overflow-x-auto pb-1 -mx-1 px-1">
          {MOODS.map((mood, index) => (
            <button
              key={mood.id}
              type="button"
              onClick={() => setSelectedMood(selectedMood === mood.id ? null : mood.id)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-300 ${
                selectedMood === mood.id
                  ? `bg-gradient-to-r ${mood.color} text-white shadow-sm`
                  : `${mood.bg} ${mood.text} hover:shadow-sm`
              }`}
              style={{ transitionDelay: `${index * 30}ms` }}
            >
              {mood.label}
            </button>
          ))}
        </div>

        <div className={`relative rounded-xl transition-all duration-300 ${
          selectedMoodData ? `bg-gradient-to-br ${selectedMoodData.bg} border-2 border-transparent` : 'bg-gray-50'
        }`}>
          {selectedPrompt && (
            <div className="flex items-center justify-between px-4 pt-4">
              <p className={`text-base font-medium transition-colors duration-300 ${
                selectedMoodData ? selectedMoodData.text : 'text-gray-700'
              }`}>
                {selectedPrompt}
              </p>
              <button
                onClick={() => setSelectedPrompt(null)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {expandQuestion && (
            <div className="mx-4 mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800 flex items-start gap-2">
                <Layers className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{expandQuestion}</span>
              </p>
            </div>
          )}

          <textarea
            ref={textareaRef}
            value={entry}
            onChange={(e) => handleEntryChange(e.target.value)}
            placeholder={selectedPrompt ? "Start writing..." : "What's on your mind?"}
            rows={4}
            className="w-full bg-transparent border-0 px-4 pb-4 pt-3 text-gray-900 placeholder:text-gray-400 focus:ring-0 resize-none"
          />

          {entry.length > 0 && (
            <div className={`absolute bottom-3 right-3 text-xs font-medium px-2 py-1 rounded-full transition-all duration-300 ${
              hasMinChars
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-500'
            }`}>
              {hasMinChars ? 'Ready to save' : `${minChars - entry.trim().length} more`}
            </div>
          )}
        </div>

        {showTagSuggestions && suggestedTags.length > 0 && (
          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-xs font-medium text-blue-700 mb-2 flex items-center gap-1">
              <Tag className="h-3 w-3" />
              Suggested tags based on your entry
            </p>
            <div className="flex flex-wrap gap-1.5">
              {suggestedTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => handleTagClick(tag)}
                  className="px-2.5 py-1 text-xs font-medium bg-white text-blue-700 rounded-full border border-blue-200 hover:bg-blue-100 transition-colors"
                >
                  + {tag}
                </button>
              ))}
            </div>
          </div>
        )}

        {selectedTags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {selectedTags.map(tag => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full"
              >
                {tag}
                <button
                  onClick={() => handleTagClick(tag)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        {detectedActions.length > 0 && (
          <div className="mt-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
            <p className="text-xs font-medium text-emerald-700 mb-2 flex items-center gap-1">
              <CheckSquare className="h-3 w-3" />
              Action items detected - tap to track
            </p>
            <div className="space-y-1.5">
              {detectedActions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={() => handleActionToggle(action)}
                  className={`w-full text-left px-3 py-2 text-sm rounded-lg border transition-colors ${
                    selectedActions.includes(action)
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-800'
                      : 'bg-white border-emerald-200 text-gray-700 hover:bg-emerald-50'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className={`w-4 h-4 rounded border flex items-center justify-center ${
                      selectedActions.includes(action)
                        ? 'bg-emerald-500 border-emerald-500 text-white'
                        : 'border-gray-300'
                    }`}>
                      {selectedActions.includes(action) && (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </span>
                    {action}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="px-5 pb-5">
        <button
          onClick={handleSave}
          disabled={saving || !canSave}
          className={`w-full relative overflow-hidden inline-flex items-center justify-center px-6 py-3.5 font-semibold rounded-xl transition-all duration-300 group ${
            canSave
              ? 'bg-gray-900 text-white hover:bg-gray-800 shadow-lg shadow-gray-900/10'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          {canSave && (
            <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
          )}
          {saving ? (
            <div className="w-5 h-5 rounded-full border-2 border-white border-t-transparent animate-spin" />
          ) : (
            <>
              <Send className="h-4 w-4 mr-2 relative" />
              <span className="relative">
                {selectedActions.length > 0
                  ? `Save with ${selectedActions.length} action${selectedActions.length > 1 ? 's' : ''}`
                  : 'Save entry'}
              </span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
